import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export type TicketStatus = 'OPEN' | 'PENDING' | 'RESOLVED' | 'REJECTED';
export type Priority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

export interface InboxFilter {
  status?: string;
  channel?: string;
  /** 'ME' resolves to current user ID at query time */
  assignedTo?: string;
  inboxId?: string;
  priority?: string;
  /** INB-001: filtro de origem. 'CAMPAIGN' = view de respostas de campanha */
  origin?: 'INBOUND' | 'OUTBOUND' | 'CAMPAIGN' | 'ALL';
}

export interface SavedView {
  id: string;
  name: string;
  icon?: string;
  filter: InboxFilter;
  builtIn?: boolean;
}

const DEFAULT_SAVED_VIEWS: SavedView[] = [
  {
    id: 'mine',
    name: 'Meus Atendimentos',
    icon: 'user',
    filter: { assignedTo: 'ME' },
    builtIn: true,
  },
  {
    id: 'unassigned',
    name: 'Não Atribuídos',
    icon: 'inbox',
    filter: { assignedTo: 'UNASSIGNED' },
    builtIn: true,
  },
  {
    id: 'high-priority',
    name: 'Alta Prioridade',
    icon: 'alert',
    filter: { priority: 'HIGH' },
    builtIn: true,
  },
  { id: 'all', name: 'Todos', icon: 'list', filter: {}, builtIn: true },
  // INB-001: view de respostas de campanha
  {
    id: 'campaign-replies',
    name: 'Respostas de Campanha',
    icon: 'megaphone',
    filter: { origin: 'CAMPAIGN' },
    builtIn: true,
  },
];

export interface Ticket {
  id: string;
  conversationId: string;
  contactName?: string;
  profilePhoto?: string;
  channel?: string;
  lastMessage?: string;
  lastMessageAt: string;
  status: TicketStatus;
  priority: Priority;
  unreadCount: number;
  sentiment?: 'happy' | 'neutral' | 'angry' | 'thinking';
  slaDeadlineAt?: string;
  slaStatus?: 'PENDING' | 'WARNING' | 'BREACHED' | 'COMPLETED' | 'CANCELLED';
  viewingAgents?: string[];
  typingAgents?: string[];
  inboxId?: string;
  // INB-001: origem da conversa (CAMPAIGN = disparado por campanha)
  origin?: string;
  campaignId?: string | null;
}

export interface Message {
  id: string;
  ticketId: string;
  content: string;
  sender: 'operator' | 'customer' | 'system';
  timestamp: string;
  type: 'text' | 'image' | 'event';
  status?: 'SENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';
  isPrivate?: boolean;
}

interface InboxState {
  tickets: Ticket[];
  messages: Record<string, Message[]>; // ticketId -> Message[]
  selectedTicketId: string | null;
  isLoading: boolean;

  // T1.3: Contact Sidebar
  showContactSidebar: boolean;
  toggleContactSidebar: () => void;

  // INB-005: Sidebar colápsável
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;

  // T1.5: Saved Views / Filters
  activeFilter: InboxFilter;
  savedViews: SavedView[];
  setActiveFilter: (filter: InboxFilter) => void;
  addSavedView: (view: SavedView) => void;
  removeSavedView: (id: string) => void;

  // T2.3: Bulk selection
  selectedIds: string[];
  toggleSelectTicket: (id: string) => void;
  clearSelection: () => void;
  selectAll: () => void;

  // Actions
  setTickets: (tickets: Ticket[]) => void;
  addMessage: (ticketId: string, message: Message) => void;
  selectTicket: (id: string | null) => void;
  updateTicket: (id: string, updates: Partial<Ticket>) => void;
  addOrUpdateTicket: (ticket: Ticket) => void;
  setLoading: (loading: boolean) => void;
  // QW-07 — customer typing map: key = `${contactPhone}:${inboxId}`
  customerTypingMap: Record<string, boolean>;
  setCustomerTyping: (
    contactPhone: string,
    inboxId: string | null | undefined,
    isTyping: boolean,
  ) => void;
}

export const useInboxStore = create<InboxState>()(
  persist(
    (set) => ({
      tickets: [],
      messages: {},
      selectedTicketId: null,
      isLoading: false,
      showContactSidebar: true,
      sidebarCollapsed: false,
      activeFilter: {},
      savedViews: DEFAULT_SAVED_VIEWS,
      selectedIds: [],

      toggleContactSidebar: () =>
        set((state) => ({ showContactSidebar: !state.showContactSidebar })),

      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setActiveFilter: (filter) => set({ activeFilter: filter }),

      addSavedView: (view) => set((state) => ({ savedViews: [...state.savedViews, view] })),

      removeSavedView: (id) =>
        set((state) => ({ savedViews: state.savedViews.filter((v) => v.id !== id) })),

      toggleSelectTicket: (id) =>
        set((state) => ({
          selectedIds: state.selectedIds.includes(id)
            ? state.selectedIds.filter((s) => s !== id)
            : [...state.selectedIds, id],
        })),

      clearSelection: () => set({ selectedIds: [] }),

      selectAll: () => set((state) => ({ selectedIds: state.tickets.map((t) => t.id) })),

      setTickets: (tickets) => set({ tickets }),

      addMessage: (ticketId, msg) =>
        set((state) => {
          const ticketMsgs = state.messages[ticketId] || [];
          // Evitar duplicatas por ID
          if (ticketMsgs.some((m) => m.id === msg.id)) return state;

          return {
            messages: { ...state.messages, [ticketId]: [...ticketMsgs, msg] },
          };
        }),

      selectTicket: (id) => set({ selectedTicketId: id }),

      updateTicket: (id, updates) =>
        set((state) => ({
          tickets: state.tickets.map((t) => (t.id === id ? { ...t, ...updates } : t)),
        })),

      addOrUpdateTicket: (ticket) =>
        set((state) => {
          const exists = state.tickets.some((t) => t.id === ticket.id);
          if (exists) {
            return {
              tickets: state.tickets
                .map((t) => (t.id === ticket.id ? { ...t, ...ticket } : t))
                .sort(
                  (a, b) =>
                    new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime(),
                ),
            };
          }
          return {
            tickets: [ticket, ...state.tickets].sort(
              (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime(),
            ),
          };
        }),

      setLoading: (loading) => set({ isLoading: loading }),

      // QW-07 — customer typing
      customerTypingMap: {},
      setCustomerTyping: (contactPhone, inboxId, isTyping) => {
        const key = `${contactPhone}:${inboxId ?? ''}`;
        set((state) => ({ customerTypingMap: { ...state.customerTypingMap, [key]: isTyping } }));
      },
    }),
    {
      name: 'omniflow:inbox-storage',
      storage: createJSONStorage(() => localStorage),
      // Opcional: Persistir apenas tickets para evitar que o storage fique gigante com mensagens
      // selectedTicketId NÃO é persistido — ao abrir o app nenhuma conversa deve ser pré-selecionada
      partialize: (state) => ({
        tickets: state.tickets,
        showContactSidebar: state.showContactSidebar,
        sidebarCollapsed: state.sidebarCollapsed,
        savedViews: state.savedViews,
      }),
    },
  ),
);
